import { Link } from "react-router-dom";
import { ArrowRight, UserRound, Phone, Mail, MessageCircle } from "lucide-react";
import { advisoryBoard, boardMembers, middleManagement } from "../data/mindscope";
import { Reveal, RevealStagger, StaggerItem } from "../components/mindscope/Reveal";

function PersonCard({ name, role, credentials, phone, email, whatsapp, bio, variant = "dark" }) {
  const dark = variant === "dark";
  return (
    <div
      className={`p-8 md:p-10 h-full gold-border-hover ${
        dark ? "bg-[#14264A] text-white" : "bg-white text-ms-navy"
      }`}
    >
      <div className={`flex items-center gap-4 mb-6 ${dark ? "text-ms-gold" : "text-ms-gold-bright"}`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${dark ? "bg-ms-gold/10" : "bg-ms-navy/5"}`}>
          <UserRound size={22} strokeWidth={1.4} />
        </div>
        <div className="font-body text-[0.7rem] tracking-[0.28em] uppercase">
          {role}
          {credentials ? <span className={dark ? "text-white/60" : "text-ms-navy/50"}> · {credentials}</span> : null}
        </div>
      </div>
      <h3 className={`font-display text-3xl md:text-4xl leading-tight mb-5 ${dark ? "text-white" : "text-ms-navy"}`}>
        {name}
      </h3>
      <p className={`font-body leading-relaxed ${dark ? "text-white/70" : "text-ms-navy/70"}`}>
        {bio}
      </p>
      {(phone || email || whatsapp) && (
        <div className={`mt-6 pt-6 border-t ${dark ? "border-white/10" : "border-ms-navy/10"} space-y-2 font-body text-sm`}>
          {phone && (
            <a
              href={`tel:${phone.replace(/[^+0-9]/g, "")}`}
              className={`flex items-center gap-3 ${dark ? "text-white/80 hover:text-ms-gold" : "text-ms-navy/80 hover:text-ms-gold-bright"} transition-colors`}
            >
              <Phone size={14} strokeWidth={1.6} className={dark ? "text-ms-gold" : "text-ms-gold-bright"} />
              {phone}
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              className={`flex items-center gap-3 ${dark ? "text-white/80 hover:text-ms-gold" : "text-ms-navy/80 hover:text-ms-gold-bright"} transition-colors`}
            >
              <Mail size={14} strokeWidth={1.6} className={dark ? "text-ms-gold" : "text-ms-gold-bright"} />
              {email}
            </a>
          )}
          {whatsapp && (
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-${name.split(' ').slice(-1)[0].toLowerCase()}`}
              className={`inline-flex items-center gap-3 mt-3 px-4 py-2 rounded-full border transition-colors ${
                dark
                  ? "border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366] hover:text-ms-navy"
                  : "border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366] hover:text-white"
              }`}
            >
              <MessageCircle size={14} strokeWidth={1.8} />
              <span className="text-[0.7rem] tracking-[0.24em] uppercase">Chat on WhatsApp</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function Leadership() {
  return (
    <main data-testid="leadership-page" className="bg-ms-navy text-white overflow-hidden">
      {/* Header */}
      <section className="pt-40 pb-16 bg-ms-navy grain relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Chapter Four</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-[-0.03em]">
              Leadership & <span className="italic text-ms-gold">Governance</span>
            </h1>
            <p className="mt-8 max-w-2xl font-body text-white/70 text-lg leading-relaxed">
              A senior, principled group of practitioners — each with a personal reputation to
              protect and a mandate to earn.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Org structure visual */}
      <section className="section-y bg-ms-navy">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-10">Organisational Structure</div>
          </Reveal>
          <div className="relative h-64 md:h-80 flex items-center justify-center">
            {/* Connecting lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 200" preserveAspectRatio="none">
              <line x1="200" y1="40" x2="100" y2="160" stroke="#C9A961" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
              <line x1="200" y1="40" x2="300" y2="160" stroke="#C9A961" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
              <line x1="100" y1="160" x2="300" y2="160" stroke="#C9A961" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
            </svg>
            {[
              { top: "10%", left: "50%", label: "Advisory Board" },
              { top: "70%", left: "22%", label: "Board" },
              { top: "70%", left: "78%", label: "Middle Management" },
            ].map((n) => (
              <div
                key={n.label}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3"
                style={{ top: n.top, left: n.left }}
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-ms-gold/50 bg-ms-navy/80 flex items-center justify-center pulse-gold">
                  <UserRound size={26} className="text-ms-gold" strokeWidth={1.2} />
                </div>
                <span className="font-body text-[0.7rem] tracking-[0.24em] uppercase text-white/70 whitespace-nowrap">
                  {n.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Board */}
      <section className="section-y bg-[#050914]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-6">
              <div className="eyebrow mb-6">Advisory Board</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-white">
                Counsel that has <span className="italic text-ms-gold">read the room</span>.
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="grid md:grid-cols-2 gap-6">
            {advisoryBoard.map((p) => (
              <StaggerItem key={p.name}>
                {/* On the Leadership page, hide phone / email / WhatsApp — visitors reach principals via /contact. */}
                <PersonCard
                  name={p.name}
                  role={p.role}
                  credentials={p.credentials}
                  bio={p.bio}
                  variant="dark"
                />
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Board */}
      <section className="section-y bg-ms-paper text-ms-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-8">
              <div className="eyebrow mb-6" style={{ color: "#0A1128" }}><span style={{ color: "#0A1128" }}>Board</span></div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-ms-navy">
                Stewards of the <span className="italic">firm&apos;s conscience</span>.
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="grid md:grid-cols-2 gap-6">
            {boardMembers.map((p) => (
              <StaggerItem key={p.name}>
                <PersonCard {...p} variant="light" />
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Middle Management */}
      <section className="section-y bg-ms-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-8">
              <div className="eyebrow mb-6">Middle Management Core</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-white">
                Execution as a <span className="italic text-ms-gold">discipline</span>.
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {middleManagement.map((p) => (
              <StaggerItem key={p.name}>
                <div className="bg-ms-navy p-8 h-full gold-border-hover !border-0">
                  <div className="w-11 h-11 rounded-full bg-ms-gold/10 flex items-center justify-center mb-6 text-ms-gold">
                    <UserRound size={20} strokeWidth={1.4} />
                  </div>
                  <div className="font-body text-[0.65rem] tracking-[0.28em] uppercase text-ms-gold mb-2">
                    {p.role}
                  </div>
                  <h3 className="font-display text-2xl text-white mb-3 leading-tight">{p.name}</h3>
                  <p className="font-body text-white/65 text-sm leading-relaxed">{p.bio}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y bg-[#050914]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <Reveal>
            <div className="eyebrow mb-4">Continue</div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.95] text-white">
              Chapter Five — <span className="italic text-ms-gold">The Ecosystem</span>
            </h3>
          </Reveal>
          <Link to="/services" data-testid="leadership-next-chapter" className="btn-gold">
            See Solutions <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
